import { Button, Flex } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { ErrorText } from "@/components/errors";
import { MAX_WORKSTYLES_POINTS, MAX_WORKSTYLES_POINTS_FOR_EACH } from "@/data/consultant-profiles";
import { ConsultantProfile } from "@/data/consultant-profiles/types/ConsultantProfile";
import { WorkStyle } from "@/data/consultant-profiles/types/WorkStyle";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { Messenger } from "@/views/components/Messenger";
import { FormikApplyWorkStylesEdit } from "@/views/job-applications/apply/_workstyles/ApplyWorkStylesEdit";

import { useApplyWizard } from "../_components/ApplyWizardContext";
import { useApplyWizardDraftUpdate } from "../_components/useApplyWizardDraftUpdate";

export interface WorkStylesFormValues {
  workStyles?: WorkStyle[];
}

interface IWorkstylesFormSectionProps {
  form: FormikProps<WorkStylesFormValues>;
}

export const WORKSTYLES_MESSENGER_CONTEXT = "profile-workStyles-form";

export const workStylesValidation = Yup.object()
  .required()
  .shape({
    workStyles: Yup.array()
      .of(
        Yup.object().shape({
          score: Yup.number().max(MAX_WORKSTYLES_POINTS_FOR_EACH).min(0),
          type: Yup.string().required(),
        }),
      )
      .test("21-points-test", "21 points error", (workstyles: WorkStyle[]) => {
        const total = workstyles.reduce((sum, workstyle) => sum + workstyle.score, 0);
        return total === MAX_WORKSTYLES_POINTS;
      }),
  });

export const transformDraftWithWorkstyles =
  (values: WorkStylesFormValues | undefined, profile: ConsultantProfile) => (draft: JobApplication) => ({
    ...draft,
    workStyles: values?.workStyles?.length
      ? values.workStyles
      : draft.workStyles?.length
      ? draft.workStyles
      : profile!.workStyles,
  });

interface ApplyWorkstylesFormProps extends IWorkstylesFormSectionProps {}

export const ApplyWorkstylesForm: FC<ApplyWorkstylesFormProps> = memo(({ form }) => {
  const { t } = useTranslation("session");

  const { profile } = useApplyWizard();

  useApplyWizardDraftUpdate(transformDraftWithWorkstyles(form.values, profile!));

  return (
    <Flex flexDirection="column" gap="md">
      <Messenger context={WORKSTYLES_MESSENGER_CONTEXT} />
      <Flex flexDirection="column">
        <Flex flexDirection="column" gap="md">
          <FormikApplyWorkStylesEdit
            name="workStyles"
            value={form.values.workStyles!}
            setFieldTouched={form.setFieldTouched}
          />

          {form.errors.workStyles && form.submitCount > 0 && (
            <ErrorText error={t("CONSULTANT_PROFILES.DETAIL.WORK_STYLES.ERROR")} />
          )}
        </Flex>
      </Flex>
      <Flex>
        <Button onClick={form.submitForm} disabled={form.isSubmitting} size="lg">
          {t("JOB_REQUESTS.APPLY.WIZARD.CONTINUE")}
        </Button>
      </Flex>
    </Flex>
  );
});
